/**
 * Constructor Identity — shared client/session ID helpers
 *
 * Single source of truth for the identity values sent with Constructor
 * search/browse/autocomplete requests, kept in sync with Constructor's
 * official JS client (the tracking beacon loaded by the constructor-connect
 * app embed) so Constructor can link search requests to tracked user actions.
 *
 * Mirrors the official client's @constructor-io/constructorio-id defaults:
 *   - client ID  → cookie `ConstructorioID_client_id`
 *                  (localStorage fallback `_constructorio_search_client_id`)
 *   - session ID → localStorage `_constructorio_search_session_id` + JSON
 *                  `_constructorio_search_session` ({ sessionId, lastTime })
 *   - new session after 30 minutes of inactivity
 *
 * Exposes `window.ConstructorIdentity`:
 *   - getClientId()            → anonymous client ID (`i` param)
 *   - getSessionNumber()       → current session number (`s` param)
 *   - appendIdentityParams(p)  → sets i, s, _dt (+ ui for logged-in customers)
 */
(function () {
    const CLIENT_ID_KEY = 'ConstructorioID_client_id';
    const CLIENT_ID_LOCAL_KEY = '_constructorio_search_client_id';
    const SESSION_ID_KEY = '_constructorio_search_session_id';
    const SESSION_DATA_KEY = '_constructorio_search_session';
    const SESSION_COOKIE_KEY = 'ConstructorioID_session';
    const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const CLIENT_ID_COOKIE_DAYS = 365;

    function getCookie(name) {
        try {
            const prefix = name + '=';
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                let cookie = cookies[i];
                while (cookie.charAt(0) === ' ') cookie = cookie.substring(1);
                if (cookie.indexOf(prefix) === 0) {
                    const value = cookie.substring(prefix.length);
                    try {
                        return decodeURIComponent(value);
                    } catch {
                        return value;
                    }
                }
            }
        } catch {
            // Fall through to empty result
        }
        return '';
    }

    function setCookie(name, value) {
        const expires = new Date(Date.now() + CLIENT_ID_COOKIE_DAYS * 24 * 60 * 60 * 1000);
        document.cookie =
            name +
            '=' +
            encodeURIComponent(value) +
            '; expires=' +
            expires.toUTCString() +
            '; path=/';
    }

    /**
     * Get or create the persistent anonymous client ID shared with the
     * Constructor tracking beacon. The official JS client stores this value
     * in the `ConstructorioID_client_id` cookie; we read/write the same
     * cookie (adopting the official localStorage fallback key if present)
     * instead of generating our own storage-local ID.
     */
    function getClientId() {
        try {
            let id = getCookie(CLIENT_ID_KEY);
            if (!id) {
                // Adopt an ID already generated by the official client in localStorage.
                try {
                    id = localStorage.getItem(CLIENT_ID_LOCAL_KEY) || '';
                } catch {
                    // localStorage might be disabled or inaccessible
                }
            }
            if (!id) {
                const hasCrypto =
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
                id = hasCrypto
                    ? crypto.randomUUID()
                    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                          const r = (Math.random() * 16) | 0;
                          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
                      });
            }
            // Persist to the cookie so the beacon client picks up the same value.
            setCookie(CLIENT_ID_KEY, id);
            return id;
        } catch {
            return '';
        }
    }

    /**
     * Get the current session number, mirroring the official client's logic:
     * the number increments when the last activity timestamp stored in
     * `_constructorio_search_session` is older than SESSION_TIMEOUT_MS.
     */
    function getSessionNumber() {
        try {
            const now = Date.now();
            let sessionData = null;

            try {
                const raw = localStorage.getItem(SESSION_DATA_KEY);
                if (raw) {
                    sessionData = JSON.parse(raw);
                }
            } catch {
                // Ignore localStorage read errors
            }

            if (!sessionData || typeof sessionData !== 'object') {
                // Legacy cookie fallback (mirrors the official client's migration).
                const cookieData = getCookie(SESSION_COOKIE_KEY);
                if (cookieData) {
                    try {
                        sessionData = JSON.parse(cookieData);
                    } catch {
                        sessionData = null;
                    }
                }
            }

            let sessionId = 1;
            let sessionDataId = 1;
            if (sessionData && typeof sessionData === 'object') {
                sessionDataId = parseInt(sessionData.sessionId, 10) || 1;
                sessionId =
                    sessionData.lastTime > now - SESSION_TIMEOUT_MS
                        ? sessionDataId
                        : sessionDataId + 1;
            }
            const isNew = !sessionData || sessionDataId !== sessionId;

            const storedData = { sessionId, lastTime: now };
            if (isNew) storedData.newToBeacon = true;

            try {
                localStorage.setItem(SESSION_ID_KEY, sessionId);
                localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(storedData));
            } catch {
                // Ignore localStorage write errors
            }
            return sessionId;
        } catch {
            return 1;
        }
    }

    /**
     * Append identity params (i, s, _dt, ui) to a URLSearchParams instance.
     * `ui` is only sent for logged-in customers. Returns the same params
     * object for chaining.
     */
    function appendIdentityParams(params) {
        params.set('i', getClientId());
        params.set('s', getSessionNumber());
        params.set('_dt', Date.now().toString());
        if (window.cnstrc?.userId) {
            params.set('ui', window.cnstrc.userId);
        }
        return params;
    }

    window.ConstructorIdentity = {
        getClientId,
        getSessionNumber,
        appendIdentityParams
    };
})();
